/**
 * CommunityFeed — the full feed with Nova woven in
 * Assembles CommunityPostCard, NovaThreadParticipant, NovaFeedInsert
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { NOVA_VOICE } from '../data/novaCommunityConfig';
import { detectSpiral, getChapterPatternHeadline, getChapterPatternBody } from '../data/novaCommunityConfig';
import { NovaThreadParticipant } from './NovaThreadParticipant';
import { NovaFeedInsert } from './NovaFeedInsert';
import { CHAPTER_THEMES as CHAPTER_THEMES_DATA } from '../data/chapterThemes';

function initials(str) {
  const s = String(str || '').trim();
  if (!s) return '?';
  return s.split(/\s+/).map((p) => p[0]).join('').toUpperCase().slice(0, 2);
}

/** CommunityPostCard — individual post with Nova reply injected */
function CommunityPostCard({
  post,
  theme,
  chapter,
  signal,
  onOpenNova,
  onLearnMore,
  profileBasePath,
  feedSkin,
}) {
  const [expanded, setExpanded] = useState(false);
  const showNovaReply = signal && signal.confidence !== 'low';
  const replies = Array.isArray(post.replies) ? post.replies : [];
  const replyCount = replies.length;
  const repliesAboveThreshold = replyCount >= 5;
  const showRepliesSection = (expanded || repliesAboveThreshold || showNovaReply) && (replies.length > 0 || showNovaReply);

  const t = theme || CHAPTER_THEMES_DATA[chapter] || CHAPTER_THEMES_DATA.fertility;
  const isFertilitySkin = feedSkin === 'fertilityCommunity';

  if (isFertilitySkin) {
    return (
      <article className="com-post-card">
        <div className="com-post-inner">
          <div className="com-post-author">
            <div className="com-post-av">{post.authorInitial || initials(post.authorName || post.name)}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              {profileBasePath ? (
                <Link to={`${profileBasePath}/${(post.authorSlug || post.authorName || post.name || '').toLowerCase()}`} className="com-post-name">
                  {post.authorName || post.name}
                </Link>
              ) : (
                <span className="com-post-name">{post.authorName || post.name}</span>
              )}
              <span className="com-post-time">{post.timeAgo || post.date}</span>
            </div>
          </div>
          <p className="com-post-text">{post.text}</p>
          <div className="com-post-reactions">
            <span className="com-post-react">❤️ {post.likes ?? post.hearts ?? 0}</span>
            {post.sameBoat != null && <span className="com-post-react">🤝 same boat {post.sameBoat}</span>}
            <button type="button" className="com-post-reply-inline" onClick={() => setExpanded(!expanded)}>
              💬 {replyCount > 0 ? `${replyCount} replies` : 'Reply'}
            </button>
            <button type="button" className="com-post-reply-end" onClick={() => setExpanded(!expanded)}>
              Reply
            </button>
          </div>
        </div>

        {showRepliesSection && showNovaReply && (
          <NovaThreadParticipant
            chapter={chapter}
            spiral={signal}
            originalPostText={post.text}
            onOpenNova={onOpenNova}
            onLearnMore={onLearnMore}
            feedSkin="fertilityCommunity"
          />
        )}

        {showRepliesSection && replies.length > 0 && (
          <div className="com-thread-replies">
            {replies.slice(0, expanded ? undefined : 2).map((reply, ri) => (
              <div key={reply.id || ri} className="com-thread-row">
                <div className="com-thread-av">{reply.authorInitial || initials(reply.authorName || reply.name)}</div>
                <div>
                  <div className="com-thread-name">{reply.authorName || reply.name}</div>
                  <p className="com-thread-text">{reply.text}</p>
                </div>
              </div>
            ))}

            {!expanded && replies.length > 2 && (
              <button type="button" className="com-thread-more" onClick={() => setExpanded(true)}>
                See {replies.length - 2} more replies
              </button>
            )}
          </div>
        )}
      </article>
    );
  }

  return (
    <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, marginBottom: 10, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: post.avatarBg || t.accentBg,
              color: post.avatarColor || t.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {post.authorInitial || initials(post.authorName || post.name)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {profileBasePath ? (
              <Link to={`${profileBasePath}/${(post.authorSlug || post.authorName || post.name || '').toLowerCase()}`} style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', textDecoration: 'none' }}>
                {post.authorName || post.name}
              </Link>
            ) : (
              <span style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22' }}>{post.authorName || post.name}</span>
            )}
            {post.authorTag && (
              <span style={{ fontSize: 10, fontWeight: 500, color: 'rgba(60,30,45,0.6)', background: 'var(--color-background-secondary)', padding: '2px 8px', borderRadius: 999 }}>
                {post.authorTag}
              </span>
            )}
            <span style={{ fontSize: 11, color: 'rgba(60,30,45,0.4)' }}>{post.timeAgo || post.date}</span>
          </div>
        </div>
        <p style={{ fontSize: 13, color: '#2d1a22', lineHeight: 1.55, margin: 0 }}>{post.text}</p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--color-text-tertiary)' }}>
            <span>❤️ {post.likes ?? post.hearts ?? 0}</span>
            {post.sameBoat != null && <span>🤝 same boat {post.sameBoat}</span>}
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: 12,
                fontWeight: 500,
                color: t.accentText,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
            >
              💬 {replyCount > 0 ? `${replyCount} replies` : 'Reply'}
            </button>
          </div>
          <button
            type="button"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontSize: 12,
              fontWeight: 500,
              color: t.accentText,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
          >
            Reply
          </button>
        </div>
      </div>

      {showRepliesSection && (
        <div style={{ borderTop: '1px solid var(--border)', background: 'var(--color-background-secondary)', padding: '12px 20px 16px' }}>
          {showNovaReply && (
            <div style={{ marginBottom: 12 }}>
              <NovaThreadParticipant
                chapter={chapter}
                spiral={signal}
                originalPostText={post.text}
                onOpenNova={onOpenNova}
                onLearnMore={onLearnMore}
              />
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {replies.slice(0, expanded ? undefined : 2).map((reply, ri) => (
              <div key={reply.id || ri} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: 'var(--white)',
                    border: '0.5px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    fontWeight: 500,
                    color: 'var(--color-text-tertiary)',
                    flexShrink: 0,
                  }}
                >
                  {reply.authorInitial || initials(reply.authorName || reply.name)}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22' }}>{reply.authorName || reply.name}</span>
                    <span style={{ fontSize: 10, color: 'rgba(60,30,45,0.4)' }}>{reply.timeAgo || '2h ago'}</span>
                  </div>
                  <p style={{ fontSize: 13, color: '#2d1a22', lineHeight: 1.5, margin: 0 }}>{reply.text}</p>
                </div>
              </div>
            ))}

            {!expanded && replies.length > 2 && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: 11,
                  fontWeight: 500,
                  color: t.accentText,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  alignSelf: 'flex-start',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
              >
                See {replies.length - 2} more replies
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** CommunityFeed — full feed with Nova woven in */
export function CommunityFeed({
  theme,
  chapter = 'fertility',
  items,
  onOpenNova,
  profileBasePath,
  feedSkin,
}) {
  const t = theme || CHAPTER_THEMES_DATA[chapter] || CHAPTER_THEMES_DATA.fertility;
  let postIndex = -1;

  const wrapStyle = feedSkin === 'fertilityCommunity' ? undefined : { display: 'flex', flexDirection: 'column', gap: 10 };
  const wrapClass = feedSkin === 'fertilityCommunity' ? 'com-feed-stack' : undefined;

  return (
    <div className={wrapClass} style={wrapStyle || { display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((item, index) => {
        if (item.novaInsert) {
          return (
            <NovaFeedInsert
              key={item.id || `ora-${index}`}
              chapter={item.chapter || chapter}
              insertType={item.insertType}
              headline={item.headline}
              body={item.body}
              tag={item.tag}
              cta={item.cta ? { ...item.cta, onClick: item.cta.onClick || onOpenNova } : undefined}
              onOpenNova={onOpenNova}
            />
          );
        }

        postIndex += 1;
        const signal = item.threadData?.spiral ?? detectSpiral(item);
        const replyCount = Array.isArray(item.replies) ? item.replies.length : (item.threadData?.userReplies?.length ?? item.replies ?? 0);
        const isGriefHighReply = signal?.type === 'grief_contagion' && signal?.confidence === 'high' && replyCount >= 10;
        const showPatternNotice = postIndex > 0 && postIndex % 5 === 0;

        const post = {
          id: item.id,
          authorName: item.authorName || item.name,
          authorInitial: item.authorInitial || initials(item.name),
          authorTag: item.authorTag,
          timeAgo: item.timeAgo || item.date,
          text: item.text,
          likes: item.likes ?? item.hearts,
          sameBoat: item.sameBoat,
          replies: item.threadData?.userReplies?.map((r, i) => ({
            id: `r-${i}`,
            authorName: r.name,
            authorInitial: initials(r.name),
            timeAgo: '2h ago',
            text: r.text,
          })) ?? [],
          avatarBg: item.avatarBg,
          avatarColor: item.avatarColor,
          authorSlug: item.authorSlug,
        };

        return (
          <div key={post.id || index}>
            <CommunityPostCard
              post={post}
              theme={t}
              chapter={chapter}
              signal={signal}
              onOpenNova={onOpenNova}
              onLearnMore={onOpenNova}
              profileBasePath={profileBasePath}
              feedSkin={feedSkin}
            />

            {isGriefHighReply && (
              <NovaFeedInsert
                theme={t}
                chapter={chapter}
                insertType="circuit_breaker"
                headline=""
                body={NOVA_VOICE[chapter]?.circuitBreaker ?? NOVA_VOICE.fertility.circuitBreaker}
                onOpenNova={onOpenNova}
              />
            )}

            {showPatternNotice && (
              <NovaFeedInsert
                theme={t}
                chapter={chapter}
                insertType="pattern_notice"
                tag="FOR YOU"
                headline={getChapterPatternHeadline(chapter)}
                body={getChapterPatternBody(chapter)}
                cta={{ label: NOVA_VOICE[chapter]?.redirectCTA ?? NOVA_VOICE.fertility.redirectCTA, onClick: onOpenNova }}
                onOpenNova={onOpenNova}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default CommunityFeed;
